import React, { useEffect, useRef, useState } from 'react';
import styles from './Trading.module.css';
import compactStyles from './CompactTable.module.css';
import { TickL1 } from '../../types/trading';
import { TickerDropdown } from '../common/TickerDropdown';
import { SymbolLogo } from '../common/SymbolLogo';
import { useStockMonitorPrevClose } from './useStockMonitorPrevClose';
import { Trash2 } from 'lucide-react';

export interface StockMonitorProps {
  ticksBySymbol: Record<string, TickL1>;
  onSubscribe: (symbol: string) => void;
  onUnsubscribe: (symbol: string) => void;
}

const DEFAULT_SYMBOLS = ['AAPL'];

const normalizeSymbol = (symbol: string): string => symbol.trim().toUpperCase();

const formatPrice = (value: number | null | undefined): string => {
  if (value == null) return '--';
  return value.toFixed(2);
};

/** Return the PnL CSS class for a signed numeric value, or empty string for zero/null. */
const pnlClass = (value: number | null | undefined): string => {
  if (value == null || value === 0) return '';
  return value > 0 ? compactStyles.pnlPositive : compactStyles.pnlNegative;
};

const relativeClass = (
  value: number | null | undefined,
  baseline: number | null | undefined,
): string => {
  if (value == null || baseline == null) return '';
  if (value === baseline) return '';
  return value > baseline ? compactStyles.pnlPositive : compactStyles.pnlNegative;
};

const formatSigned = (value: number): string => {
  if (value > 0) return `+${value.toFixed(2)}`;
  return value.toFixed(2);
};

export const StockMonitor: React.FC<StockMonitorProps> = ({
  ticksBySymbol,
  onSubscribe,
  onUnsubscribe,
}) => {
  const [symbols, setSymbols] = useState<string[]>(DEFAULT_SYMBOLS);
  const [pendingSymbol, setPendingSymbol] = useState('');
  const subscribedSymbolsRef = useRef<Set<string>>(new Set());
  const prevCloseBySymbol = useStockMonitorPrevClose(symbols);

  useEffect(() => {
    const previous = subscribedSymbolsRef.current;
    const next = new Set(symbols);

    next.forEach((symbol) => {
      if (!previous.has(symbol)) {
        onSubscribe(symbol);
      }
    });

    previous.forEach((symbol) => {
      if (!next.has(symbol)) {
        onUnsubscribe(symbol);
      }
    });

    subscribedSymbolsRef.current = next;
  }, [symbols, onSubscribe, onUnsubscribe]);

  useEffect(() => () => {
    subscribedSymbolsRef.current.forEach((symbol) => onUnsubscribe(symbol));
    subscribedSymbolsRef.current.clear();
  }, [onUnsubscribe]);

  const addSymbol = (rawSymbol: string) => {
    const symbol = normalizeSymbol(rawSymbol);
    if (!symbol) return;

    setSymbols((prev) => {
      if (prev.includes(symbol)) return prev;
      return [...prev, symbol];
    });
    setPendingSymbol('');
  };

  const removeSymbol = (symbol: string) => {
    setSymbols((prev) => prev.filter((current) => current !== symbol));
  };

  return (
    <section className={`${styles.card} ${styles.tradingTheme}`}>
      <header className={styles.cardHeader}>Stock Monitor</header>

      <div className={styles.inlineForm}>
        <TickerDropdown
          value={pendingSymbol}
          onChange={setPendingSymbol}
          onCommit={setPendingSymbol}
          placeholder="Select stock symbol"
          testId="stock-monitor-add-input"
        />
        <button
          type="button"
          className={styles.buttonPrimary}
          onClick={() => addSymbol(pendingSymbol)}
          disabled={!pendingSymbol}
          data-testid="stock-monitor-add-button"
        >
          Add
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={compactStyles.compactTable}>
          <thead>
            <tr>
              <th>Symbol</th>
              <th className={compactStyles.numericCell}>Bid</th>
              <th className={compactStyles.numericCell}>Ask</th>
              <th className={compactStyles.numericCell}>Last</th>
              <th className={compactStyles.numericCell}>Prev</th>
              <th className={compactStyles.numericCell}>Change</th>
              <th className={compactStyles.numericCell}>% Change</th>
              <th className={styles.actionHeader}>Action</th>
            </tr>
          </thead>
          <tbody>
            {symbols.length === 0 && (
              <tr>
                <td colSpan={8} className={styles.emptyCell}>Add symbols to monitor</td>
              </tr>
            )}
            {symbols.map((symbol) => {
              const tick = ticksBySymbol[symbol];
              const prev = prevCloseBySymbol[symbol];
              const prevClose = prev?.status === 'ok' ? prev.prev_close : null;
              const hasMid = tick?.bid != null && tick?.ask != null;
              const mid = hasMid ? ((tick?.bid as number) + (tick?.ask as number)) / 2 : null;
              const change = prevClose != null && mid != null ? mid - prevClose : null;
              const changePct =
                change != null && prevClose != null && prevClose !== 0
                  ? (change / prevClose) * 100
                  : null;

              return (
                <tr key={symbol} data-testid={`stock-monitor-row-${symbol}`}>
                  <td data-testid="stock-monitor-symbol-cell">
                    <span className={compactStyles.symbolCellContent}>
                      <SymbolLogo symbol={symbol} size={16} />
                      <span className={compactStyles.symbolCellLabel}>{symbol}</span>
                    </span>
                  </td>
                  <td
                    className={`${compactStyles.numericCell} ${relativeClass(tick?.bid, prevClose)}`}
                    data-testid={`stock-monitor-bid-${symbol}`}
                  >
                    {formatPrice(tick?.bid)}
                  </td>
                  <td
                    className={`${compactStyles.numericCell} ${relativeClass(tick?.ask, prevClose)}`}
                    data-testid={`stock-monitor-ask-${symbol}`}
                  >
                    {formatPrice(tick?.ask)}
                  </td>
                  <td className={compactStyles.numericCell} data-testid={`stock-monitor-last-${symbol}`}>{formatPrice(tick?.last)}</td>
                  <td className={compactStyles.numericCell} data-testid={`stock-monitor-prev-${symbol}`}>
                    {formatPrice(prevClose)}
                  </td>
                  <td className={`${compactStyles.numericCell} ${pnlClass(change)}`} data-testid={`stock-monitor-change-${symbol}`}>
                    {change != null ? formatSigned(change) : '--'}
                  </td>
                  <td className={`${compactStyles.numericCell} ${pnlClass(changePct)}`} data-testid={`stock-monitor-pct-change-${symbol}`}>
                    {changePct != null ? `${formatSigned(changePct)}%` : '--'}
                  </td>
                  <td className={styles.actionCell}>
                    <button
                      type="button"
                      className={`${styles.removeButton} ${styles.removeIconButton}`}
                      onClick={() => removeSymbol(symbol)}
                      aria-label={`Delete ${symbol}`}
                      title={`Delete ${symbol}`}
                      data-testid={`stock-monitor-remove-${symbol}`}
                    >
                      <Trash2 aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
